import './App.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import PasswordPage from './pages/PasswordPage';
import { SERVER_URL } from './config';

const imageTypes = [
  ['temperature', 'humidity'], 
  ['battery-voltage', 'rssi']
];

function App() {
  const [credentials, setCredentials] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(''); // State for the selected node
  const [selectedTimeRange, setSelectedTimeRange] = useState(1); // State for the time range
  const [imageUrls, setImageUrls] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [irrigation, setIrrigation] = useState(null);

  const handlePasswordSubmit = (credentials, nodes) => {
    setCredentials(credentials);
    setNodes(nodes);
    if (nodes.length > 0) {
      setSelectedNode(nodes[0]); // Automatically select the first node
    }
  };

  const handleNodeChange = (event) => {
    setSelectedNode(event.target.value);
    console.log('Selected node:', event.target.value);
    // Additional logic for handling the change can be added here
  };

  // Function to handle time range selection change
  const handleTimeRangeChange = (event) => {
    setSelectedTimeRange(event.target.value);
    console.log('Selected time range:', event.target.value);
  };

  // Available time range options
  const timeRangeOptions = [
    '1 hour', '3 hours', '6 hours', '12 hours', '24 hours',
    '3 days', '1 week', '2 weeks', '4 weeks', '3 months',
    '6 months', '12 months', '24 months'
  ];

  useEffect(() => {
    // List of image types for the grid
    const imageWidth = 600; // Example size, can be adjusted based on container width
    const imageHeight = imageWidth * 2 / 3;

    const startTime = [
      "-PT1H", "-PT3H", "-PT6H", "-PT12H", "-PT24H",
      "-PT72H", "-PT168H", "-PT336H", "-PT672H", "-PT2160H",
      "-PT4320H", "-PT8640H", "-PT17280H"
    ];

    // Function to fetch images using Basic Auth
    const fetchImages = async () => {
      if (!credentials) return;

      for (const type of imageTypes.flat()) {
        try {
          const response = await fetch(`${SERVER_URL}/${selectedNode}/${type}?start=${startTime[selectedTimeRange]}&width=${imageWidth}&height=${imageHeight}`, {
            headers: {
              'Authorization': `Basic ${credentials}`,
            },
          });
          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageUrls(prevState => ({ ...prevState, [type]: objectUrl }));
          } else {
            console.error(`Failed to load ${type} image, status: ${response.status}`);
          }
        } catch (error) {
          console.error(`Error fetching ${type} image:`, error);
        }
      }
    };
    // Initial fetch and setup interval for refreshing images
    fetchImages();

    const fetchConfig = async () => {
      if (!credentials) return;
      try {
        setIsLoading(true);
        const response = await fetch(`${SERVER_URL}/${selectedNode}/config`, {
          headers: {
            'Authorization': `Basic ${credentials}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.status}`);
        }

        const data = await response.json();
        setConfig(data);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
      }
    };
    fetchConfig();

    const interval = setInterval(fetchImages, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval); // Cleanup on unmount
  }, [selectedNode, selectedTimeRange, credentials]);

  const renderGridImages = () => {
    if (!selectedNode) return null; // Return null if no node is selected

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {imageTypes[0].map((type) => (
          <img
            key={type}
            src={imageUrls[type] || ''}
            alt={type.charAt(0).toUpperCase() + type.slice(1)}
            style={{ width: '100%', height: 'auto' }}
          />
        ))}

        {imageTypes[1].map((type) => (
          <img
            key={type}
            src={imageUrls[type] || ''}
            alt={type.charAt(0).toUpperCase() + type.slice(1)}
            style={{ width: '100%', height: 'auto' }}
          />
        ))}
      </div>
    );
  };

  // Update configuration on the server
  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      console.log(JSON.stringify(config));

      const response = await fetch(`${SERVER_URL}/${selectedNode}/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${credentials}`,
        },
        body: JSON.stringify({ ...config, target: selectedNode }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update config: ${response.status}`);
      }

    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleIrrigation = async () => {
    try {
      console.log(JSON.stringify(irrigation));

      const response = await fetch(`${SERVER_URL}/${selectedNode}/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${credentials}`,
        },
        body: JSON.stringify({ target: config.target, immediate_watering: irrigation }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update config: ${response.status}`);
      }

    } catch (err) {
      console.error(err);
    }
  };
  
 // Update local configuration state
 const updateTemperature = (index, value) => {
  setConfig((prevConfig) => ({
    ...prevConfig,
    target_temperatures: prevConfig.target_temperatures.map((temp, i) =>
      i === index ? parseInt(value, 10) || 0 : temp
    ),
  }));
};

const renderCommand = () => {
  if (!selectedNode) return null; // Return null if no node is selected
  if (isLoading) return <p>Loading...</p>;

  return (
    <div>
      <h1>Immediate irrigation</h1>
      <div>
        <label>
          Duration (seconds):
          <input
            type="number"
            value={irrigation}
            onChange={(e) =>
              setIrrigation((previous) => parseInt(e.target.value, 10))
            }
            style={{ marginLeft: '10px' }}
          />
        </label>
      </div>
      <button onClick={handleIrrigation} style={{ marginTop: '20px' }}>
        Submit
      </button>
    </div>
  );
};

const updateIrrigationtime = (index, value) => {
  const [hour, minute] = value.split(':').map(Number);
  setConfig((prevConfig) => ({
    ...prevConfig,
    irrigation_schedule: prevConfig.irrigation_schedule.map((irrigation_event, i) =>
      i === index ? { ...irrigation_event, start_hour: hour, start_minute: minute } : irrigation_event
    ),
  }));
};

const updateIrrigationDuration = (index, value) => {
  const duration = Number(value);
  setConfig((prevConfig) => ({
    ...prevConfig,
    irrigation_schedule: prevConfig.irrigation_schedule.map((irrigation_event, i) =>
      i === index ? { ...irrigation_event, duration: duration } : irrigation_event
    ),
  }));
};

const updateIrrigationInside = (index, value) => {
  const inside = Boolean(value);
  setConfig((prevConfig) => ({
    ...prevConfig,
    irrigation_schedule: prevConfig.irrigation_schedule.map((irrigation_event, i) =>
      i === index ? { ...irrigation_event, inside: inside } : irrigation_event
    ),
  }));
};

const deleteIrrigationEvent = (index) => {
  setConfig((prevConfig) => ({
    ...prevConfig,
    irrigation_schedule: prevConfig.irrigation_schedule.filter((_, i) => i !== index)
  }));
};

const renderSettings = () => {
    if (!selectedNode) return null; // Return null if no node is selected
    if (isLoading) return <p>Loading...</p>;

    return (
      <div>
        <h1>Configuration</h1>
        <div>
          <h2>Irrigation Schedule</h2>
          <table border="1" style={{ textAlign: 'center' }}>
            <thead>
              <tr>
                <td>Start Time</td><td>Duration (m)</td><td>Inside</td>
              </tr>
            </thead>
            <tbody>
              {
                config.irrigation_schedule ?
                  config.irrigation_schedule.map((irrigation_event, index) =>
                    (<tr>
                      <td>
                        <input 
                          type="time" 
                          value={`${String(irrigation_event.start_hour).padStart(2, '0')}:${String(irrigation_event.start_minute).padStart(2, '0')}`}
                          onChange={(e) => {
                            console.log(e.target.value);
                            updateIrrigationtime(index, e.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input 
                          type="number" 
                          value={`${String(irrigation_event.duration)}`}
                          onChange={(e) => { updateIrrigationDuration(index, e.target.value)}}
                        />
                      </td>
                      <td>
                        <input 
                          type="checkbox"
                          checked={irrigation_event.inside}
                          onChange={(e) => { updateIrrigationInside(index, e.target.checked)}}
                        />
                      </td>
                      <td>
                        <input
                          type="button"
                          value="Delete"
                          onClick={(e) => { deleteIrrigationEvent(index) }}
                        />
                      </td>
                    </tr>)
                  ) : <tr/>
              }
              <tr>
                <input 
                  type="button" 
                  value='New entry' 
                  onClick={(e) => {
                    setConfig((prevConfig)=>
                      ({
                        ...prevConfig, 
                        irrigation_schedule: [
                          ...prevConfig.irrigation_schedule, 
                          { start_hour: 0, start_minute: 0, duration: 0, inside: false } 
                        ]
                      }))
                    }}
                />
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <h2>Target Temperatures</h2>
          <table border="1" style={{ textAlign: 'center' }}>
            <thead>
              <tr>
                {config.target_temperatures.map((_, index) => (
                  <th key={index}>{index}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {config.target_temperatures.map((temp, index) => (
                  <td key={index}>
                    <input
                      type="number"
                      value={temp}
                      onChange={(e) => updateTemperature(index, e.target.value)}
                      style={{ width: '60px' }}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <button onClick={handleSubmit} style={{ marginTop: '20px' }}>
          Submit
        </button>
      </div>
    );
  };

  return (
    <Router>
      {credentials ? (
        <div className="App">
          <header className="App-header">
            <p>
              Percy
            </p>
          </header>
          <main style={{ padding: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* Node Selector */}
              <select value={selectedNode} onChange={handleNodeChange}>
                {nodes.map((node, index) => (
                  <option key={index} value={node}>
                    {node}
                  </option>
                ))}
              </select>
              {/* Time Range Selector */}
              <select value={selectedTimeRange} onChange={handleTimeRangeChange}>
                {timeRangeOptions.map((range, index) => (
                  <option key={index} value={index}>
                    {range}
                  </option>
                ))}
              </select>
            </div>
            {renderGridImages()}
            {renderCommand()}
            {renderSettings()}
          </main>
        </div>
      ) : (
        <PasswordPage onPasswordSubmit={handlePasswordSubmit} />
      )}
    </Router>
  );
}

export default App;
